import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

const PrivacyPolicy = () => {
  const lastUpdated = 'May 23, 2026';

  return (
    <div className="min-h-screen bg-[#f9f7f3]" data-testid="privacy-policy-page">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#434345] to-[#2a2a2c] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <p className="text-xs tracking-widest uppercase text-[#BCA480] mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-sm text-gray-300">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 space-y-8 text-[#434345] leading-relaxed">

          <section>
            <p className="text-base">
              Bespoke Realty (&quot;Bespoke Realty,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) respects your privacy
              and is committed to protecting the personal information you share with us. This Privacy Policy
              explains what information we collect, how we use it, when we share it, and the choices you have
              regarding your information when you visit{' '}
              <a href="https://bespokerealty.net" className="text-[#BCA480] hover:underline">bespokerealty.net</a>{' '}
              or interact with us by phone, email, or text message.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">1. Information We Collect</h2>
            <p className="mb-2">We collect information that you voluntarily provide, including:</p>
            <ul className="list-disc pl-6 space-y-1 text-base">
              <li><strong>Contact details</strong>: name, email address, phone number, mailing address.</li>
              <li><strong>Real estate preferences</strong>: property type, budget range, preferred locations, timeline,
                tour requests, and other details submitted through our forms.</li>
              <li><strong>Transaction information</strong>: parties to a transaction (buyers, sellers, lenders,
                title companies), contract dates, and communication history when we represent you.</li>
              <li><strong>Account credentials</strong> for agents and administrators accessing our portal.</li>
              <li><strong>Communications</strong> you send to us by email, SMS, chat, or web form.</li>
            </ul>
            <p className="mt-3">We also automatically collect limited technical data (IP address, browser type,
              pages visited) for site analytics and security.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1 text-base">
              <li>To respond to inquiries, schedule tours, and provide brokerage services.</li>
              <li>To coordinate real estate transactions, including reminders and follow-ups from our AI
                Transaction Coordinator (&quot;Auri&quot;).</li>
              <li>To send you confirmation emails after you submit a form.</li>
              <li>To send transactional SMS messages (e.g., closing reminders, document requests) when you
                have opted in. See Section 4 below.</li>
              <li>To improve our website, services, and customer experience.</li>
              <li>To comply with legal, regulatory, and licensing obligations in Mississippi, Tennessee, and Arkansas.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">3. How We Share Information</h2>
            <p>We do <strong>not</strong> sell or rent your personal information. We share it only with:</p>
            <ul className="list-disc pl-6 space-y-1 text-base mt-2">
              <li><strong>Licensed agents and staff</strong> of Bespoke Realty assisting with your transaction.</li>
              <li><strong>Service providers</strong> who help us operate the business — including Resend (email),
                Twilio (SMS), SimplyRETS (MLS listings), and OpenAI (AI assistants). These providers are
                bound by contractual confidentiality obligations.</li>
              <li><strong>Parties to your transaction</strong> when necessary — such as lenders, title companies,
                inspectors, and the opposing side&apos;s broker.</li>
              <li><strong>Legal authorities</strong> when required by law, subpoena, or to protect rights and safety.</li>
            </ul>
            <p className="mt-3 font-semibold text-[#BCA480]">
              We do not share, sell, or transfer SMS opt-in information or phone numbers to third parties or
              affiliates for marketing purposes.
            </p>
          </section>

          <section id="sms">
            <h2 className="text-2xl font-bold mb-3">4. SMS / Text Messaging Program</h2>
            <p className="mb-2">
              <strong>Program name:</strong> Bespoke Realty SMS Program.
            </p>
            <p className="mb-2">
              By opting in on our website (via the SMS consent checkbox on our lead-capture forms), by
              providing your phone number in person, or by signing a buyer/seller representation
              agreement, you consent to receive recurring text messages from Bespoke Realty,
              including:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-base">
              <li>Tour confirmations and scheduling updates.</li>
              <li>Transaction milestone reminders (inspections, appraisals, closing dates).</li>
              <li>Document requests and signature follow-ups.</li>
              <li>Responses from our AI Transaction Coordinator and licensed agents.</li>
            </ul>
            <p className="mt-3"><strong>Message frequency:</strong> Recurring messages, up to 10 messages per month depending on your active tours and transactions.</p>
            <p className="mt-2"><strong>Message and data rates may apply</strong> from your mobile carrier. Carriers are not liable for delayed or undelivered messages.</p>
            <p className="mt-2">
              <strong>Opt-out:</strong> Reply <code className="bg-gray-100 px-1.5 py-0.5 rounded">STOP</code>{' '}
              to any of our messages at any time. For help, reply <code className="bg-gray-100 px-1.5 py-0.5 rounded">HELP</code>{' '}
              or contact us at <a href="tel:9013904414" className="text-[#BCA480] hover:underline">901-390-4414</a> or{' '}
              <a href="mailto:office@bespokerealty.net" className="text-[#BCA480] hover:underline">office@bespokerealty.net</a>.
            </p>
            <p className="mt-2">
              <strong>Consent to receive SMS messages is not a condition of purchase</strong> or of using our real estate services.
            </p>
            <p className="mt-2">
              <strong>Privacy of mobile information:</strong> No mobile information will be shared with
              third parties or affiliates for marketing or promotional purposes. Information sharing
              related to SMS originator opt-in data and consent will not be shared with any third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">5. Cookies &amp; Analytics</h2>
            <p>
              We use cookies and similar technologies to remember your preferences, keep you signed in, and
              measure site performance. You may disable cookies in your browser settings; some features of
              the site may not function without them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">6. Data Retention &amp; Security</h2>
            <p>
              We retain your information as long as your account or transaction is active, and afterward as
              required to comply with our legal, accounting, and licensing obligations. We use industry-standard
              safeguards (encryption in transit, access controls, secure third-party providers) to protect
              your information, but no system is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">7. Your Rights</h2>
            <p>Depending on your state of residence, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-1 text-base mt-2">
              <li>Request access to the personal information we hold about you.</li>
              <li>Request correction or deletion of your information.</li>
              <li>Opt out of marketing emails (link provided in every marketing email).</li>
              <li>Opt out of SMS messages (reply STOP).</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, email us at{' '}
              <a href="mailto:info@bespokerealty.net" className="text-[#BCA480] hover:underline">info@bespokerealty.net</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">8. Children&apos;s Privacy</h2>
            <p>
              Our services are not directed to children under 13, and we do not knowingly collect personal
              information from children. If you believe a child has provided us information, contact us and
              we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the
              &quot;Last Updated&quot; date at the top of this page. Material changes will be posted prominently
              on the site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">10. Contact Us</h2>
            <p>
              Questions about this Privacy Policy? Reach us at:
            </p>
            <div className="mt-3 bg-[#faf8f4] border-l-4 border-[#BCA480] p-4 rounded-r">
              <p><strong>Bespoke Realty</strong></p>
              <p>7927 Players Forest Drive, Memphis, TN 38119</p>
              <p>Phone: <a href="tel:9013904414" className="text-[#BCA480] hover:underline">901-390-4414</a></p>
              <p>Email: <a href="mailto:info@bespokerealty.net" className="text-[#BCA480] hover:underline">info@bespokerealty.net</a></p>
            </div>
          </section>

          <section className="pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              See also our{' '}
              <Link to="/terms" className="text-[#BCA480] hover:underline" data-testid="link-to-terms">
                Terms &amp; Conditions
              </Link>.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
